import { debounce } from "lodash-es";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getPanelElement,
  getPanelGroupElement,
  type ImperativePanelHandle,
} from "react-resizable-panels";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import CodePanel from "@/components/CodeEditors/CodePanel";
import LeftPanel from "@/components/Layout/LeftPanel";
import RightStyle from "@/components/RightStyle";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { cn } from "@/lib/utils";
import {
  useAiContextStore,
  useCodeDiffSetup,
} from "@/store/zustand/aiContextStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { useActiveProject } from "@/utils/utils";
import EngSpecTab from "../AI/EngSpec/EngSpec";
import { FileChangeDetails } from "../AI/EngSpec/FileChangeDetails";
import PRDTab from "../AI/PRD/PRD";
import CanvasV2 from "../Canvas/CanvasV2";
import { ConsoleV2 } from "../ConsoleV2";
import {
  calculatePanelSizes,
  calculateResizePercentages,
  getLeftPanelMaxSize,
  getLeftPanelMinSize,
  isLeftExtensionVisible,
  isRightPanelVisible,
  type PanelState,
  percentagesToPixelWidths,
} from "./panelCalculations";

const PanelsLayout = React.memo(() => {
  // Setup code diff files when needed
  useCodeDiffSetup();

  const project = useActiveProject();

  // Layout changes
  const leftExpanded = useLayoutStore((state) => state.leftExpanded);
  const expandLeft = useLayoutStore((state) => state.expandLeft);
  const rightExpanded = useLayoutStore((state) => state.rightExpanded);
  const expandRight = useLayoutStore((state) => state.expandRight);
  const updateSize = useLayoutStore((state) => state.updateSize);

  // Panel resize state management
  const setIsPanelResizing = useIframePoolStore(
    (state) => state.setIsPanelResizing,
  );

  // Handle resize drag events
  const handleResizeDragging = useCallback(
    (isDragging: boolean) => {
      setIsPanelResizing(isDragging);
    },
    [setIsPanelResizing],
  );

  const layoutMode = useLayoutStore((state) => state.layoutMode);
  const leftTab = useLayoutStore((state) => state.leftTab);

  const leftPanelSize = useLayoutStore((state) => state.leftPanelSize);

  const leftPanelExtensionSize = useLayoutStore(
    (state) => state.leftPanelExtensionSize,
  );
  const rightPanelSize = useLayoutStore((state) => state.rightPanelSize);

  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const leftPanelRef = useRef<ImperativePanelHandle | null>(null);
  const leftPanelExtensionRef = useRef<ImperativePanelHandle | null>(null);
  const canvasPanelRef = useRef<ImperativePanelHandle | null>(null);
  const rightPanelRef = useRef<ImperativePanelHandle | null>(null);

  // Get fullscreen state directly from the store
  // (CanvasV2 already manages the fullscreen hook internally)
  const isFullScreenEnabled = useIframePoolStore(
    (state) => state.fullscreenStoryboardId !== null,
  );

  // Track panel visibility to prevent updates when panels change
  const leftExtensionVisible = isLeftExtensionVisible(
    leftExpanded,
    layoutMode,
    leftTab,
  );

  const rightPanelVisible = isRightPanelVisible(
    rightExpanded,
    layoutMode,
    isFullScreenEnabled,
  );

  // Flag to prevent feedback loops and track window width changes
  const isUpdatingFromResize = useRef(false);
  const lastWindowWidth = useRef(window.innerWidth);

  // Store the target pixel widths that should be maintained
  const targetPixelWidths = useRef({
    left: 0,
    leftExt: 0,
    right: 0,
    savedAt: 0, // timestamp when saved
  });

  // Calculate minimum size percentages for panels based on screen width
  const [leftPanelMinSize, setLeftPanelMinSize] = useState(15); // fallback
  const [leftPanelMaxSize, setLeftPanelMaxSize] = useState(50); // fallback
  const [rightPanelMinSize, setRightPanelMinSize] = useState(12); // fallback
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Eng spec info
  const openedFileIsCommand = useAiContextStore(
    (state) => state.engSpecOpenedFileIsCommand,
  );

  const aiChatEngSpecOpen = useAiContextStore(
    (state) => state.aiChatEngSpecOpen,
  );

  // Calculate correct panel sizes for current layout state
  const calculatedPanelSizes = useMemo(() => {
    const panelState: PanelState = {
      leftExpanded,
      rightExpanded,
      layoutMode,
      leftTab,
      leftPanelSize,
      leftPanelExtensionSize,
      rightPanelSize,
    };
    return calculatePanelSizes(panelState);
  }, [
    leftExpanded,
    rightExpanded,
    layoutMode,
    leftTab,
    leftPanelSize,
    leftPanelExtensionSize,
    rightPanelSize,
    leftExtensionVisible, // Add this dependency to recalculate when visibility changes
    rightPanelVisible, // Also add right panel visibility
  ]);

  /**
   * Apply calculated panel sizes to the actual panels using collapse/expand
   * This runs after the sizes are calculated and panels are ready
   */
  useEffect(() => {
    // Wait for required panels to load (only check panels that should be rendered)
    const requiredPanelsReady =
      leftPanelRef.current &&
      canvasPanelRef.current &&
      (!leftExtensionVisible || leftPanelExtensionRef.current) &&
      (!rightPanelVisible || rightPanelRef.current);

    if (!requiredPanelsReady) {
      return;
    }

    // Resize panels to calculated sizes, using 0 for hidden panels
    leftPanelRef.current?.resize(
      leftExpanded ? calculatedPanelSizes.leftPanelSize : 0,
    );
    leftPanelExtensionRef.current?.resize(
      leftExtensionVisible ? calculatedPanelSizes.leftPanelExtensionSize : 0,
    );
    canvasPanelRef.current?.resize(calculatedPanelSizes.canvasSize);
    if (rightPanelRef.current) {
      rightPanelRef.current?.resize(
        rightPanelVisible ? calculatedPanelSizes.rightPanelSize : 0,
      );
    }
  }, [calculatedPanelSizes, leftExtensionVisible, rightPanelVisible]);

  // Additional effect to enforce size constraints when visibility changes
  useEffect(() => {
    if (!leftExtensionVisible && leftPanelExtensionRef.current) {
      // Force the left extension panel to size 0 when it shouldn't be visible
      leftPanelExtensionRef.current.resize(0);
    }
  }, [leftExtensionVisible]);

  // Immediate synchronous constraint application using useLayoutEffect
  React.useLayoutEffect(() => {
    if (!leftExtensionVisible && leftPanelExtensionRef.current) {
      // Apply the constraint immediately before paint to prevent any flashing
      leftPanelExtensionRef.current.resize(0);
    }
  }, [leftExtensionVisible]);

  const rawHandleLayoutChange = useCallback(
    (layout: number[]) => {
      // Handle different layout array lengths based on which panels are rendered
      // Layout can be [left, leftExt, canvas] or [left, leftExt, canvas, right]
      if (layout.length < 3 || layout.length > 4) {
        return;
      }

      const newPanelSizes = {
        leftPanelSize: layout[0],
        leftPanelExtensionSize: layout[1],
        canvasSize: layout[2],
        rightPanelSize: layout.length === 4 ? layout[3] : 0,
      };

      // Save current pixel widths when user is actively resizing (not during window resize)
      const now = Date.now();
      const timeSinceSaved = now - targetPixelWidths.current.savedAt;
      const isUserDragging =
        !isUpdatingFromResize.current && timeSinceSaved > 500;

      if (isUserDragging) {
        const containerWidth =
          document.getElementById("panels-layout-container")?.clientWidth ||
          window.innerWidth;

        // Save pixel widths using extracted utility
        const pixelWidths = percentagesToPixelWidths(
          newPanelSizes,
          containerWidth,
        );
        targetPixelWidths.current.left = pixelWidths.left;
        targetPixelWidths.current.leftExt = pixelWidths.leftExt;
        targetPixelWidths.current.right = pixelWidths.right;
        targetPixelWidths.current.savedAt = now;
      }

      // Only save sizes when panels are actually visible and user is interacting with them
      // Don't save programmatically set sizes (like 0 for hidden panels)
      if (leftExpanded && newPanelSizes.leftPanelSize > 0) {
        updateSize("leftPanelSize", newPanelSizes.leftPanelSize);
      }

      // Only save left extension size if it's visible and has a real size
      if (leftExtensionVisible && newPanelSizes.leftPanelExtensionSize > 0) {
        updateSize(
          "leftPanelExtensionSize",
          newPanelSizes.leftPanelExtensionSize,
        );
      }

      // Only save right panel size if it's visible and has a real size
      if (rightPanelVisible && newPanelSizes.rightPanelSize > 0) {
        updateSize("rightPanelSize", newPanelSizes.rightPanelSize);
      }
      // Canvas size doesn't need to be saved as it's always the remaining space
    },
    [updateSize, leftExtensionVisible, rightPanelVisible, leftExpanded],
  );

  const throttledHandleLayoutChange = useMemo(
    () =>
      debounce(rawHandleLayoutChange, 200, { leading: false, trailing: true }),
    [rawHandleLayoutChange],
  );

  // Resize observer to track screen width changes and maintain panel sizes
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    let previousWidth = 0;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target.id === "panels-layout-container") {
          const overallWidth = entry.contentRect.width;

          if (overallWidth > 0) {
            // Update minimum and maximum sizes when panel group resizes
            const leftMinSize = getLeftPanelMinSize(leftTab, overallWidth);
            setLeftPanelMinSize(leftMinSize);

            const leftMaxSize = getLeftPanelMaxSize(leftTab, overallWidth);
            setLeftPanelMaxSize(leftMaxSize);

            const rightMinSizePercentage = Math.max(
              5,
              (240 / overallWidth) * 100,
            );
            setRightPanelMinSize(rightMinSizePercentage);

            // Only maintain pixel widths on actual window resizes, not internal panel changes
            const windowWidth = window.innerWidth;
            const isActualWindowResize =
              Math.abs(windowWidth - lastWindowWidth.current) > 50;

            if (
              previousWidth > 0 &&
              Math.abs(overallWidth - previousWidth) > 10 &&
              isActualWindowResize &&
              !isUpdatingFromResize.current &&
              targetPixelWidths.current.savedAt > 0 // Only if we have saved target widths
            ) {
              isUpdatingFromResize.current = true;

              // Use extracted utility to calculate new percentages
              const newSizes = calculateResizePercentages(
                targetPixelWidths.current,
                overallWidth,
                { leftMinSize, rightMinSize: rightMinSizePercentage },
                { leftExpanded, leftExtensionVisible, rightPanelVisible },
              );

              // Update all sizes to match target pixel widths
              if (leftExpanded && targetPixelWidths.current.left > 0) {
                updateSize("leftPanelSize", newSizes.leftPanelSize);
              }
              if (
                leftExtensionVisible &&
                targetPixelWidths.current.leftExt > 0
              ) {
                updateSize(
                  "leftPanelExtensionSize",
                  newSizes.leftExtensionSize,
                );
              }
              if (rightPanelVisible && targetPixelWidths.current.right > 0) {
                updateSize("rightPanelSize", newSizes.rightPanelSize);
              }

              lastWindowWidth.current = windowWidth;

              // Reset flag after updates complete
              setTimeout(() => {
                isUpdatingFromResize.current = false;
              }, 50);
            }

            previousWidth = overallWidth;
          }
        }
      }
    });

    // Observe the parent container element (which has full screen width)
    const containerElement = document.getElementById("panels-layout-container");
    if (containerElement) {
      resizeObserver.observe(containerElement);
      // Initialize previousWidth
      previousWidth = containerElement.clientWidth;
    }

    // Also listen to window resize for initial setup
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [leftTab]);

  // Initial size calculation on mount and initialize target pixel widths
  useEffect(() => {
    const overallWidth =
      getPanelGroupElement("overall-panel-group")?.clientWidth ||
      document.getElementById("panels-layout-container")?.clientWidth;

    if (!overallWidth) {
      // If overallWidth is not available, don't try to calculate initial sizes based on it.
      // Persisted values (or defaults from the other useEffect) will take precedence.
      return;
    }

    // Initialize target pixel widths from saved percentages
    if (targetPixelWidths.current.savedAt === 0) {
      if (leftPanelSize > 0) {
        targetPixelWidths.current.left = Math.round(
          (leftPanelSize / 100) * overallWidth,
        );
      }
      if (leftPanelExtensionSize > 0) {
        targetPixelWidths.current.leftExt = Math.round(
          (leftPanelExtensionSize / 100) * overallWidth,
        );
      }
      if (rightPanelSize > 0) {
        targetPixelWidths.current.right = Math.round(
          (rightPanelSize / 100) * overallWidth,
        );
      }
      if (
        targetPixelWidths.current.left > 0 ||
        targetPixelWidths.current.leftExt > 0 ||
        targetPixelWidths.current.right > 0
      ) {
        targetPixelWidths.current.savedAt = Date.now();
      }
    }

    // On first load, set the sizes IF they are not already set by persistence (i.e., are 0)
    if (leftPanelSize === 0) {
      const calculatedSize =
        ((getPanelElement("left-panel")?.clientWidth || 0) / overallWidth) *
        100;
      if (calculatedSize > 0) updateSize("leftPanelSize", calculatedSize);
    }
    if (leftPanelExtensionSize === 0) {
      const calculatedSize =
        ((getPanelElement("left-panel-extension")?.clientWidth || 0) /
          overallWidth) *
        100;
      if (calculatedSize > 0)
        updateSize("leftPanelExtensionSize", calculatedSize);
    }
    // Canvas size is derivative, usually not set directly here unless others are 0.
    if (rightPanelSize === 0) {
      const calculatedSize =
        ((getPanelElement("right-panel")?.clientWidth || 0) / overallWidth) *
        100;
      if (calculatedSize > 0) updateSize("rightPanelSize", calculatedSize);
    }
  }, [leftPanelSize, leftPanelExtensionSize, rightPanelSize]);

  const getLeftExtensionPanel = () => {
    return (
      <>
        <div
          className={cn(
            "w-full h-full",
            layoutMode !== LayoutMode.PRD && "hidden",
          )}
        >
          <PRDTab />
        </div>

        {/* EngSpec content */}
        <div
          className={cn(
            "w-full h-full",
            (leftTab !== LeftTab.EngSpec || layoutMode === LayoutMode.PRD) &&
              "hidden",
          )}
        >
          <EngSpecTab />
        </div>

        <div
          className={cn(
            "w-full h-full",
            (leftTab === LeftTab.EngSpec || layoutMode === LayoutMode.PRD) &&
              "hidden",
          )}
        >
          <ResizablePanelGroup direction="vertical">
            {/* Otherwise the code panel + console are shown */}
            <ResizablePanel defaultSize={70} collapsible={true}>
              <CodePanel />
            </ResizablePanel>
            <ResizableHandle withHandle onDragging={handleResizeDragging} />

            <ResizablePanel defaultSize={30} collapsible={true}>
              <ConsoleV2 />
              {/* Always connect so that there is one console tracking errors */}
              {/* <Console connect={true} /> */}
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </>
    );
  };

  const getCanvasPanel = () => {
    // For eng spec mode, there are different views
    const inEngSpecMode =
      layoutMode === LayoutMode.Code && leftTab === LeftTab.EngSpec;

    // Show the file details
    const showFileDetails = inEngSpecMode && aiChatEngSpecOpen;

    // Show the terminal along with file details
    const showTerminalForEngSpec =
      showFileDetails &&
      openedFileIsCommand &&
      layoutMode === LayoutMode.Code &&
      leftTab === LeftTab.EngSpec;

    return (
      <div className="flex flex-col h-full">
        <div className={cn("flex grow", showFileDetails && "hidden")}>
          <div className="middle-container relative flex flex-col h-full">
            {/* <Canvas /> */}
            <CanvasV2 />
          </div>
        </div>
        <div
          className={cn(
            "w-full flex flex-col max-w-full grow",
            !showFileDetails && "hidden",
          )}
        >
          <ResizablePanelGroup direction="vertical">
            <ResizablePanel defaultSize={50} className="flex grow">
              <FileChangeDetails />
            </ResizablePanel>

            <ResizableHandle
              withHandle
              onDragging={handleResizeDragging}
              className={cn(!showTerminalForEngSpec && "hidden")}
            />

            <ResizablePanel
              defaultSize={50}
              className={cn(!showTerminalForEngSpec && "hidden")}
            >
              <ConsoleV2 />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>
    );
  };

  return (
    <div id="panels-layout-container" className="flex h-full w-full flex-col">
      <ResizablePanelGroup
        id="overall-panel-group"
        direction="horizontal"
        onLayout={throttledHandleLayoutChange}
      >
        {/* Left Panel */}
        <ResizablePanel
          id="left-panel"
          ref={leftPanelRef}
          defaultSize={calculatedPanelSizes.leftPanelSize}
          minSize={leftPanelMinSize}
          maxSize={leftPanelMaxSize}
        >
          <div className="h-full z-10000">
            <LeftPanel />
          </div>
        </ResizablePanel>

        {/* ResizableHandle - but disable when left extension is not visible */}
        <ResizableHandle
          withHandle={leftExtensionVisible}
          onDragging={handleResizeDragging}
          className={
            !leftExtensionVisible
              ? "pointer-events-none opacity-0 cursor-default"
              : ""
          }
          style={
            !leftExtensionVisible
              ? { pointerEvents: "none", userSelect: "none" }
              : {}
          }
        />

        {/* Left Panel Extension */}
        <ResizablePanel
          id="left-panel-extension"
          className="z-10000 transition"
          ref={leftPanelExtensionRef}
          defaultSize={calculatedPanelSizes.leftPanelExtensionSize}
          collapsible={true}
          minSize={0}
          maxSize={leftExtensionVisible ? undefined : 0}
        >
          {getLeftExtensionPanel()}
        </ResizablePanel>
        <ResizableHandle withHandle onDragging={handleResizeDragging} />

        {/* Canvas Panel */}
        <ResizablePanel
          id={"canvas-panel"}
          ref={canvasPanelRef}
          defaultSize={calculatedPanelSizes.canvasSize}
        >
          {getCanvasPanel()}
        </ResizablePanel>

        {/* Right Panel - always render but hide when not visible */}
        <>
          <ResizableHandle
            withHandle={rightPanelVisible}
            onDragging={handleResizeDragging}
            className={
              !rightPanelVisible
                ? "pointer-events-none opacity-0 cursor-default hidden"
                : ""
            }
          />
          <ResizablePanel
            id="right-panel"
            className={`z-1002 ${!rightPanelVisible ? "hidden" : ""}`}
            ref={rightPanelRef}
            defaultSize={calculatedPanelSizes.rightPanelSize}
            minSize={rightPanelVisible ? rightPanelMinSize : 0}
            maxSize={rightPanelVisible ? undefined : 0}
          >
            <RightStyle />
          </ResizablePanel>
        </>
      </ResizablePanelGroup>

      {!leftExpanded && (
        <button
          className="absolute top-1/2 left-0 transform -translate-y-1/2 bg-secondary-300 text-white p-2 rounded-r-md hover:bg-secondary-400 z-100002"
          onClick={() => expandLeft()}
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      )}

      {!rightExpanded &&
        layoutMode === LayoutMode.Design &&
        !isFullScreenEnabled && (
          <button
            className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-secondary-300 text-white p-2 rounded-l-md hover:bg-secondary-400 z-100002"
            onClick={() => expandRight()}
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
        )}
    </div>
  );
});

export default PanelsLayout;
